import { ChangeDetectorRef, Component } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { LeadershipService } from './leadership.service';
import { LeadershipRequest } from './leadership.model';

@Component({
  selector: 'app-leadership',
  standalone: false,
  templateUrl: './leadership.component.html',
  styleUrl: './leadership.component.css'
})
export class LeadershipComponent {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];

  leaderships: any[] = [];
  filteredLeaderships: any[] = [];

  currentPage = 1;
  pageSize = 5;

  activeTab = 'all';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;

  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  leadershipId = 0;
  order = 0;
  levelId = 1;
  isActive = true;
  image: File | null = null;
  imagePreview = '';

  titleKh = '';
  firstNameKh = '';
  lastNameKh = '';
  positionKh = '';

  titleEn = '';
  firstNameEn = '';
  lastNameEn = '';
  positionEn = '';

  constructor(
    private leadershipService: LeadershipService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadLeadership('');
  }

  loadLanguages() {
    this.languageService.getAll().subscribe((res: any) => {
      this.languages = res.data.sort((a: any, b: any) => a.languageId - b.languageId);

      if (this.languages.length > 0) {
        this.setTab(this.languages[0].code);
        this.setTabPopup(this.languages[0].languageId);
      }
    });
  }

  loadLeadership(langCode: string) {
    this.leadershipService.getAllLeadership(langCode).subscribe((res: any) => {
      const apiData: any[] = res.data || [];

      this.leaderships = apiData
        .filter(item => item.translations && item.translations.length > 0)
        .flatMap(item =>
          item.translations.map((t: any) => ({
            leadershipId: item.leadershipId,
            image: item.image,
            order: item.order,
            levelId: this.parseLevelId(item.levelId),
            isActive: item.isActive,
            languageCode: t.languageCode,
            languageId: t.languageId,
            title: t.title,
            firstName: t.firstName,
            lastName: t.lastName,
            position: t.position
          }))
        );

      this.filterLeadership(this.activeTab || 'kh');
    });
  }

  setTab(tab: string) {
    this.activeTab = tab;
    this.currentPage = 1;

    this.filterLeadership(tab);
  }

  setTabPopup(tab: number) {
    this.activeTabPopup = tab;
  }

  filterLeadership(tab: string) {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredLeaderships = this.leaderships.filter(leadership => {
      const levelName = leadership.levelId === 1
        ? 'Minister'
        : leadership.levelId === 2
          ? 'Secretary of State'
          : 'Under Secretary of State';
      const status = leadership.isActive ? 'Active' : 'Inactive';
      const matchesTab = leadership.languageCode === tab;
      const matchesSearch = !keyword ||
        `${leadership.title ?? ''} ${leadership.firstName ?? ''} ${leadership.lastName ?? ''} ${leadership.position ?? ''} ${levelName} ${status} ${leadership.languageCode ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  getLanguageCount(lang: string): number {
    return this.leaderships.filter(r => r.languageCode === lang).length;
  }

  get pagedLeadership(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredLeaderships.slice(start, end);
  }

  changePage(page: number) {
    this.currentPage = page;
  }

  totalPages(): number {
    return Math.ceil(this.filteredLeaderships.length / this.pageSize);
  }

  visibleButtonControl(eventName: string) {
    this.isEnableSave = false;
    this.isEnableUpdate = false;
    this.isEnableDelete = false;

    if (eventName === 'Add') { this.isEnableSave = true; }
    else if (eventName === 'Edit') { this.isEnableUpdate = true; }
    else if (eventName === 'Delete') { this.isEnableDelete = true; }
  }

  openModal(eventName?: string) {
    if (eventName) {
      this.visibleButtonControl(eventName);
    }
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  viewData(id: number, eventName: string) {
    this.leadershipId = id;

    this.leadershipService.getLeadershipById(id, '').subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          const data = res.data;

          this.leadershipId = data.leadershipId;
          this.order = data.order;
          this.levelId = this.parseLevelId(data.levelId);
          this.isActive = data.isActive;
          this.image = null;
          this.imagePreview = data.image || '';

          data.translations.forEach((item: any) => {
            if (item.languageCode === 'kh') {
              this.titleKh = item.title;
              this.firstNameKh = item.firstName;
              this.lastNameKh = item.lastName;
              this.positionKh = item.position;
            } else if (item.languageCode === 'en') {
              this.titleEn = item.title;
              this.firstNameEn = item.firstName;
              this.lastNameEn = item.lastName;
              this.positionEn = item.position;
            }
          });

          this.openModal(eventName);
        } else {
          this.errorMessage = res.message;
        }

        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      },
      error: (err) => {
        this.errorMessage = 'Failed to load leadership data';
        setTimeout(() => {
          this.clearMsg();
          this.cdr.detectChanges();
        }, 3000);
      }
    });
  }

  onFileSelected(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.image = file;
      this.imagePreview = URL.createObjectURL(file);
    }
  }

  getRequest(leadershipId?: number): LeadershipRequest {
    return {
      image: this.image instanceof File ? this.image : undefined,
      data: {
        leadershipId: leadershipId ?? 0,
        order: this.order,
        levelId: this.levelId,
        isActive: this.isActive,
        translations: [
          {
            languageId: 1,
            title: this.titleKh ?? '',
            firstName: this.firstNameKh ?? '',
            lastName: this.lastNameKh ?? '',
            position: this.positionKh ?? ''
          },
          {
            languageId: 2,
            title: this.titleEn ?? '',
            firstName: this.firstNameEn ?? '',
            lastName: this.lastNameEn ?? '',
            position: this.positionEn ?? ''
          }
        ]
      }
    };
  }

  resetForm() {
    this.leadershipId = 0;
    this.order = 0;
    this.levelId = 1;
    this.isActive = true;
    this.image = null;
    this.imagePreview = '';
    this.titleKh = '';
    this.firstNameKh = '';
    this.lastNameKh = '';
    this.positionKh = '';
    this.titleEn = '';
    this.firstNameEn = '';
    this.lastNameEn = '';
    this.positionEn = '';
  }

  private parseLevelId(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }

      const normalized = value.trim().toLowerCase();
      switch (normalized) {
        case 'minister':
          return 1;
        case 'secretaryofstate':
        case 'secretary of state':
          return 2;
        case 'undersecretaryofstate':
        case 'under secretary of state':
          return 3;
      }
    }

    return 1;
  }

  refreshData(status: string, message: string) {
    if (status === 'success') {
      this.successMessage = message;
      this.closeModal();
      this.loadLeadership('');
      this.resetForm();
    } else {
      this.errorMessage = message;
    }

    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  clearMsg() {
    this.successMessage = '';
    this.errorMessage = '';
  }

  exceptionData(message: string) {
    this.errorMessage = message;
    setTimeout(() => {
      this.clearMsg();
      this.cdr.detectChanges();
    }, 3000);
  }

  addNew() {
    this.resetForm();
    this.openModal('Add');
  }

  save() {
    const request = this.getRequest(0);
    this.clearMsg();

    this.leadershipService.create(request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  update() {
    this.clearMsg();

    const request = this.getRequest(this.leadershipId);
    this.leadershipService.update(this.leadershipId, request).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }

  delete() {
    if (!confirm('Are you sure you want to delete ?')) {
      return;
    }

    this.leadershipService.delete(this.leadershipId).subscribe({
      next: (res) => {
        this.refreshData(res.status, res.message);
      },
      error: (err) => {
        this.exceptionData(err);
      }
    });
  }
}
